import axios from 'axios'
import router from '@/router'
import store from '@/store'
import { getApiBaseUrl, joinUrl } from '@/config/runtimeConfig'

let isRefreshing = false
let pendingRequests = []
const headers = {
  'Content-type': 'application/json',
  'access-control-allow-origin': '*'
}

const axiosInstance = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: process.env.VUE_APP_API_TIMEOUT,
  headers
})

const auth = (config) => {
  const accessToken = store.getters['auth/accessToken']
  const tempToken = store.getters['auth/temporalToken']
  if (accessToken || tempToken) {
    const aux = config.headers ? { ...config.headers } : {}
    config.headers = Object.assign(aux, {
      Authorization: 'Bearer ' + (accessToken ?? tempToken)
    })
  }
  return config
}

const refreshTokenOrFail = (error) => {
  if (error.status === null || error.status === undefined) {
    // error.status = 401
  }
  if (!error.response) {
    return Promise.reject(error)
  }
  const { config, response: { status } } = error
  const originalRequest = config

  if (status === 401) {
    if (isRefreshing) {
      return new Promise((resolve) => {
        return addPendingRequest((token) => {
          originalRequest.headers.Authorization = 'Bearer ' + token
          return resolve(axios.request(originalRequest))
        })
      })
    }
    return retry(originalRequest)
  } if (status === 403) {
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({ respuesta: null, blnError: true, codError: status, msgError: error.response.data })
  } else {
    store.commit('setIsLoading', false)
    store.commit('setAjaxRequestPending', false)
    store.commit('setLastAjaxResult', 'ko')
  }

  return Promise.reject(error)
}

const retry = (originalRequest) => {
  isRefreshing = true

  const uri = joinUrl(getApiBaseUrl(), 'api/login/refresh')
  const data = {
    Token: store.getters['auth/refreshToken']
  }

  return axios.post(uri, data).then((response) => {
    if (response.status === 200) {
      if (response.data.Token !== null) {
        const accessToken = response.data.Token
        const refreshToken = response.data.RefreshToken
        store.commit('auth/setTokens', { accessToken, refreshToken })
        retryPendingWithNewToken(accessToken)
        originalRequest.headers.Authorization = 'Bearer ' + accessToken
        isRefreshing = false
        pendingRequests = []
        return axios.request(originalRequest)
      } else {
        isRefreshing = false
        clearTokensAndReturnToLogin()
      }
    }
  }).catch(() => {
    isRefreshing = false
    clearTokensAndReturnToLogin()
  })
}

const addPendingRequest = (callback) => {
  pendingRequests.push(callback)
}

const retryPendingWithNewToken = (accessToken) => {
  pendingRequests = pendingRequests.filter(callback => callback(accessToken))
}

// Actualización de token de acceso, para llamadas que no son interceptadas por axios (ej: deep-chat)
export const refreshAccessToken = async () => {
  try {
    const uri = joinUrl(getApiBaseUrl(), 'api/login/refresh')
    const data = { Token: store.getters['auth/refreshToken'] }
    const response = await axios.post(uri, data)
    if (response?.status === 200 && response?.data?.Token) {
      const accessToken = response.data.Token
      const refreshToken = response.data.RefreshToken
      store.commit('auth/setTokens', { accessToken, refreshToken })
      return accessToken
    }
    throw new Error('Error al refrescar, con refresco externo')
  } catch (err) {
    clearTokensAndReturnToLogin()
    throw err
  }
}

const clearTokensAndReturnToLogin = () => {
  store.commit('auth/removeUser')
  store.commit('auth/removeTokens')
  store.dispatch('app/resetColors')
  router.push({ name: process.env.VUE_APP_REDIRECT_FAILED_AUTHENTICATION, params: { refreshTokenFail: true } })
}

const responseHandler = (response) => {
  store.commit('setAjaxRequestPending', false)
  store.commit('setLastAjaxResult', 'ok')
  return response
}

axiosInstance.interceptors.request.use(auth, error => Promise.reject(error))
axiosInstance.interceptors.response.use(responseHandler, refreshTokenOrFail)
// axiosInstance.defaults.headers.common['Accept-Language'] = 'en-US'
axiosInstance.defaults.headers.common['Accept-Language'] = store.getters['app/selectedLang'] || 
  process.env.VUE_APP_I18N_LOCALE ||
  'es-ES'

export default axiosInstance
